
import { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCurrentUserProfile } from "@/services/profileService";
import { useToast } from "@/hooks/use-toast";
import { sendWelcomeEmail } from "@/utils/supabase/notificationHelpers";
import Layout from "@/components/Layout";
import { useAuth } from '@/contexts/auth';
import { buildProfileCompletionRedirect, isGoogleAuthUser, needsProfileCompletion } from '@/utils/auth/profileCompletion';

const hasOAuthTokensInUrl = () =>
  window.location.hash.includes('access_token') ||
  window.location.search.includes('access_token') ||
  window.location.search.includes('provider_token');

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);

  useLayoutEffect(() => {
    if (!hasOAuthTokensInUrl()) {
      return;
    }

    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  useEffect(() => {
    const handleAuthCallback = async () => {
      if (loading) {
        return;
      }

      try {
        if (user) {
          const profile = await fetchCurrentUserProfile(user.id);
          const requiresCompletion = needsProfileCompletion(user, profile);

          if (requiresCompletion) {
            if (isGoogleAuthUser(user)) {
              // Send welcome email if this is the first time completing profile
              sendWelcomeEmail(user.id);
              toast({
                title: "Welcome!",
                description: "Please complete your profile to get started.",
              });
            }

            navigate(buildProfileCompletionRedirect());
            return;
          }
          
          // Profile is complete and user is active, go to home
          navigate("/");
        } else {
          navigate("/auth");
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate("/auth");
      } finally {
        setIsProcessing(false);
      }
    };

    void handleAuthCallback();
  }, [loading, navigate, toast, user]);

  if (isProcessing) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-16">
          <div className="rounded-md border border-brand-cloud bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-brand-ink"></div>
            <p className="text-brand-slate">Signing you in...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return null;
}
